import React, { useState } from 'react';
import { HelpOutline, Close } from '@material-ui/icons';
import { Formik } from 'formik';
import {
    TextField,
    withStyles,
    Select,
    MenuItem,
    FormControl,
    Button,
    InputLabel,
    Tooltip,
    IconButton,
    FormControlLabel,
    FormGroup,
    Checkbox,
    CircularProgress
} from '@material-ui/core';
import * as Yup from 'yup';
import JsonMappingHelp from './JsonMappingHelp';
import ProductTypePanels from './ProductTypePanels';
import FormStep2 from './FormStep2';

const styles = theme => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2)
    },
    button: {
        width: '120px',
        marginTop: '10px',
        marginRight: '10px'
    },
    formControl: {
        width: '100%'
    }
});

const FormStep1Database = props => {
    const {
        classes,
        isDirty,
        edit,
        handleSubmit,
        initialValues,
        translations,
        formSettings
    } = props;

    const [mappingHelp, setMappingHelp] = useState(false);
    const handleMappingHelp = () => {
        setMappingHelp(!mappingHelp);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape(formSettings.Validation)}
            onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);
                await handleSubmit(values);
            }}
            validateOnMount={true}
        >
            {formikProps => {
                const { values, dirty, touched, errors, isValid, isSubmitting, handleChange, handleBlur, handleSubmit } = formikProps;

                return (
                    <div className={classes.root}>
                        <form onSubmit={handleSubmit}>
                            <>
                                <TextField
                                    name="Name"
                                    value={values.Name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.Name && touched.Name)}
                                    placeholder={translations["name"] || "Name"}
                                    label={translations["name"] || "Name"}
                                    margin={"dense"}
                                    fullWidth={true}
                                />
                                <FormControl id="direction_select" className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations['please_select_direction'] || "Please select direction"}</InputLabel>
                                    <Select
                                        name="Direction"
                                        label={translations['direction'] || 'Direction'}
                                        margin={"dense"}
                                        value={values.Direction}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value={"in"}>
                                            {translations['in'] || 'in'}
                                        </MenuItem>
                                        <MenuItem value={"out"}>
                                            {translations['out'] || 'out'}
                                        </MenuItem>
                                        <MenuItem value={"in+out"}>
                                            {`${translations['in'] || 'in'}+${translations['out'] || 'out'}`}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl id="active_select" className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations["active"] || "Active"}</InputLabel>
                                    <Select
                                        name="Active"
                                        value={values.Active}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.Active && touched.Active)}
                                        placeholder={translations["active"] || "Active"}
                                        label={translations["active"] || "Active"}
                                        margin={"dense"}
                                        fullWidth={true}
                                    >
                                        <MenuItem value={true}>
                                            {translations['active'] || 'Active'}
                                        </MenuItem>
                                        <MenuItem value={false}>
                                            {translations['inactive'] || 'Inactive'}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl id="service_type_select" className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations['select_service_type'] || "Please select service type"}</InputLabel>
                                    <Select
                                        name="ServiceType"
                                        label={translations['service_type'] || 'Service type'}
                                        margin={"dense"}
                                        value={values.ServiceType === '' ? 'staging_v1' : values.ServiceType}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value={"staging_v1"}>
                                            staging_v1
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    name="DatabaseName"
                                    value={values.DatabaseName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.DatabaseName && touched.DatabaseName)}
                                    placeholder={translations["database_name"] || "Database name"}
                                    label={translations["database_name"] || "Database name"}
                                    margin={"dense"}
                                    fullWidth={true}
                                />
                                <TextField
                                    name="Hostname"
                                    value={values.Hostname}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.Hostname && touched.Hostname)}
                                    placeholder={translations["hostname"] || "Hostname"}
                                    label={translations["hostname"] || "Hostname"}
                                    margin={"dense"}
                                    fullWidth={true}
                                />
                                <TextField
                                    name="Port"
                                    value={values.Port}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    type='number'
                                    error={!!(errors.Port && touched.Port)}
                                    placeholder={translations['port'] || 'Port'}
                                    label={translations['port'] || 'Port'}
                                    margin={'dense'}
                                    fullWidth={true}
                                />
                                <TextField
                                    name="TableName"
                                    value={values.TableName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.TableName && touched.TableName)}
                                    placeholder={translations["table_name"] || "Table name"}
                                    label={translations["table_name"] || "Table name"}
                                    margin={"dense"}
                                    fullWidth={true}
                                />
                                <ProductTypePanels
                                    externalType={values.Type}
                                    translations={translations}
                                    values={values}
                                    errors={errors}
                                    touched={touched}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                />
                                <FormGroup row>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.RetryFailedCalls}
                                                onChange={handleChange}
                                                name="RetryFailedCalls"
                                                color="primary"
                                            />
                                        }
                                        label={translations['retry_failed_calls'] || 'Retry failed calls'}
                                    />
                                    {values.RetryFailedCalls ?
                                        <TextField
                                            name="MaxRetryCount"
                                            value={values.MaxRetryCount}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            type='number'
                                            error={!!(errors.MaxRetryCount && touched.MaxRetryCount)}
                                            placeholder={translations['max_retry_count'] || 'Max retry count'}
                                            label={translations['max_retry_count'] || 'Max retry count'}
                                            margin={'dense'}
                                        /> : null
                                    }
                                </FormGroup>
                                {mappingHelp ? <Tooltip title={translations["close_mapping_help"] || "Close mapping help screen"}>
                                    <IconButton onClick={handleMappingHelp}>

                                        <Close />
                                    </IconButton>
                                </Tooltip>
                                    :
                                    <Tooltip title={translations["need_help_mapping"] || "Need help with table mapping?"}>
                                        <IconButton onClick={handleMappingHelp}>

                                            <HelpOutline />
                                        </IconButton>
                                    </Tooltip>
                                }
                                {
                                    mappingHelp ?
                                        <JsonMappingHelp />
                                        : null
                                }
                                <TextField
                                    name="TableMapping"
                                    value={values.TableMapping}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    multiline={true}
                                    error={!!(errors.TableMapping && touched.TableMapping)}
                                    placeholder={translations["table_mapping"] || "Table mapping"}
                                    label={translations["table_mapping"] || "Table mapping"}
                                    margin={"dense"}
                                    fullWidth={true}
                                />
                            </>
                            <FormStep2 handleSubmit={handleSubmit} 
                                initialValues={initialValues} 
                                translations={translations} 
                                formSettings={formSettings} 
                                values={values} 
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                errors={errors}
                                touched={touched}
                                isSubmitting={isSubmitting}
                                isValid={isValid}
                                dirty={dirty}
                            />
                            {formSettings.ButtonSubmit ? (
                                <Button
                                    className={classes.submit}
                                    variant="contained"
                                    color={formSettings.ButtonSubmit.Color}
                                    type="submit"
                                    disabled={!edit ? !isValid || isSubmitting : !((isValid && dirty) || isDirty) || isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                            formSettings.ButtonSubmit.Title
                                        )}
                                </Button>
                            ) : null}
                        </form>
                    </div>
                );
            }}
        </Formik>
    );
};

export default withStyles(styles, { withTheme: true })(FormStep1Database);